class AtlasLabRankingStats extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-atlas-count]'));
    if (!this.items.length) return;

    this.duration = Number.parseInt(this.dataset.duration || '1800', 10);
    this.locale = this.dataset.locale || 'en-US';
    this.animationFrame = null;
    this.isAnimating = false;

    if (!window.matchMedia('(prefers-reduced-motion: no-preference)').matches) {
      this.setFinalValues();
      return;
    }

    this.resetValues();

    if (!('IntersectionObserver' in window)) {
      this.animate();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.animate();
          } else {
            this.stopAnimation();
            this.resetValues();
          }
        });
      },
      { threshold: 0.28 }
    );

    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.stopAnimation();
    this.observer?.disconnect();
  }

  animate() {
    if (this.isAnimating) return;

    this.stopAnimation();
    this.resetValues();
    this.isAnimating = true;

    const start = performance.now();
    const duration = Number.isFinite(this.duration) ? this.duration : 1800;

    const tick = (now) => {
      const elapsed = Math.min((now - start) / duration, 1);
      const progress = 1 - Math.pow(1 - elapsed, 3);

      this.items.forEach((item) => {
        const target = this.parseTarget(item.dataset.target);
        const decimals = Number.parseInt(item.dataset.decimals || '0', 10);
        item.textContent = this.formatNumber(target * progress, decimals);
      });

      if (elapsed < 1) {
        this.animationFrame = window.requestAnimationFrame(tick);
      } else {
        this.setFinalValues();
        this.isAnimating = false;
        this.animationFrame = null;
      }
    };

    this.animationFrame = window.requestAnimationFrame(tick);
  }

  stopAnimation() {
    if (this.animationFrame) {
      window.cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
    this.isAnimating = false;
  }

  resetValues() {
    this.items.forEach((item) => {
      const decimals = Number.parseInt(item.dataset.decimals || '0', 10);
      item.textContent = this.formatNumber(0, decimals);
    });
  }

  setFinalValues() {
    this.items.forEach((item) => {
      item.textContent = this.formatNumber(this.parseTarget(item.dataset.target), Number.parseInt(item.dataset.decimals || '0', 10));
    });
  }

  parseTarget(value) {
    const normalized = String(value || '0').replace(/,/g, '').trim();
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  formatNumber(value, decimals) {
    const safeDecimals = Number.isFinite(decimals) ? decimals : 0;
    return value.toLocaleString(this.locale, {
      minimumFractionDigits: safeDecimals,
      maximumFractionDigits: safeDecimals,
    });
  }
}

if (!window.customElements.get('atlas-lab-ranking-stats')) {
  window.customElements.define('atlas-lab-ranking-stats', AtlasLabRankingStats);
}
